import { createContext, useContext, useState, ReactNode } from 'react'

interface ToastMessage {
  id: string
  message: string
  type: 'error' | 'warning' | 'info'
}

interface ErrorToastContextType {
  showError: (message: string, type?: 'error' | 'warning' | 'info') => void
  dismissToast: (id: string) => void
}

const ErrorToastContext = createContext<ErrorToastContextType | undefined>(undefined)

export function useErrorToast() {
  const context = useContext(ErrorToastContext)
  if (!context) {
    throw new Error('useErrorToast must be used within ErrorToastProvider')
  }
  return context
}

export function ErrorToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const showError = (message: string, type: 'error' | 'warning' | 'info' = 'error') => {
    const id = Date.now().toString()
    setToasts((prev) => [...prev, { id, message, type }])

    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      dismissToast(id)
    }, 10000)
  }

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }

  return (
    <ErrorToastContext.Provider value={{ showError, dismissToast }}>
      {children}

      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-md">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`rounded-lg shadow-lg p-4 border animate-slide-in ${
              toast.type === 'error'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : toast.type === 'warning'
                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">
                    {toast.type === 'error' ? '❌' : toast.type === 'warning' ? '⚠️' : 'ℹ️'}
                  </span>
                  <h4
                    className={`font-semibold text-sm ${
                      toast.type === 'error'
                        ? 'text-red-800 dark:text-red-300'
                        : toast.type === 'warning'
                        ? 'text-yellow-800 dark:text-yellow-300'
                        : 'text-blue-800 dark:text-blue-300'
                    }`}
                  >
                    {toast.type === 'error' ? 'Error' : toast.type === 'warning' ? 'Warning' : 'Info'}
                  </h4>
                </div>
                <p
                  className={`text-sm whitespace-pre-wrap ${
                    toast.type === 'error'
                      ? 'text-red-700 dark:text-red-400'
                      : toast.type === 'warning'
                      ? 'text-yellow-700 dark:text-yellow-400'
                      : 'text-blue-700 dark:text-blue-400'
                  }`}
                >
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => dismissToast(toast.id)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </ErrorToastContext.Provider>
  )
}
